/**
 * Prototype-only floating dev bar. Holds the switches reviewers need while looking
 * at the prototype — today just the royalty-rates variant. Draggable, because it
 * floats over the app and will otherwise sit on top of whatever you want to see.
 *
 * Never ships to the client: it renders only when the dev auth bypass is on, the
 * same gate as the in-browser mock. Delete with the rest of
 * src/prototypes/contract-royalty-rates/ and the marked lines in main.tsx.
 */
import { useRef, useState } from "react";
import { DotsGrid } from "@untitledui/icons";
import { SegmentedControl, SegmentedControlItem } from "@/components/ui/segmented-control";
import { isDevAuthBypass } from "@/lib/devAuth";
import {
  RATES_LAYOUTS,
  RATES_VARIANTS,
  useRatesVariant,
  type RatesLayout,
  type RatesVariant,
} from "./variant";

const MARGIN = 8;

export function DevBar() {
  const { variant, setVariant, layout, setLayout } = useRatesVariant();
  const [position, setPosition] = useState({ x: MARGIN, y: MARGIN });
  const drag = useRef<{ x: number; y: number } | null>(null);

  if (!isDevAuthBypass()) return null;

  // Kept inside the viewport: dragged off the edge it would be unrecoverable
  // without clearing storage.
  const clamp = (x: number, y: number, bar: HTMLElement) => ({
    x: Math.min(Math.max(x, MARGIN), window.innerWidth - bar.offsetWidth - MARGIN),
    y: Math.min(Math.max(y, MARGIN), window.innerHeight - bar.offsetHeight - MARGIN),
  });

  return (
    <div
      className="fixed z-50 flex items-center gap-2 rounded-md border border-border
        bg-popover p-1.5 text-popover-foreground shadow-lg"
      style={{ left: position.x, top: position.y }}
    >
      {/* Drag handle — the controls themselves stay clickable. */}
      <button
        type="button"
        aria-label="Move the dev bar"
        className="cursor-grab touch-none px-0.5 text-muted-foreground active:cursor-grabbing"
        onPointerDown={(event) => {
          drag.current = { x: event.clientX - position.x, y: event.clientY - position.y };
          event.currentTarget.setPointerCapture(event.pointerId);
        }}
        onPointerMove={(event) => {
          const start = drag.current;
          const bar = event.currentTarget.parentElement;
          if (!start || !bar) return;
          setPosition(clamp(event.clientX - start.x, event.clientY - start.y, bar));
        }}
        onPointerUp={(event) => {
          drag.current = null;
          event.currentTarget.releasePointerCapture(event.pointerId);
        }}
      >
        <DotsGrid className="size-4" aria-hidden="true" />
      </button>

      <span className="text-xs font-medium text-muted-foreground">Royalty rates</span>
      <SegmentedControl
        type="single"
        value={variant}
        onValueChange={(next) => next && setVariant(next as RatesVariant)}
      >
        {Object.entries(RATES_VARIANTS).map(([key, label]) => (
          <SegmentedControlItem key={key} value={key} className="px-2 py-1 text-xs">
            {label}
          </SegmentedControlItem>
        ))}
      </SegmentedControl>

      {/* Only shown with the tab, since that is the only place a layout exists —
          a dial that changes nothing is worse than no dial. */}
      {variant === "tab" && (
        <>
          <span className="text-xs font-medium text-muted-foreground">Tab layout</span>
          <SegmentedControl
            type="single"
            value={layout}
            onValueChange={(next) => next && setLayout(next as RatesLayout)}
          >
            {Object.entries(RATES_LAYOUTS).map(([key, label]) => (
              <SegmentedControlItem key={key} value={key} className="px-2 py-1 text-xs">
                {label}
              </SegmentedControlItem>
            ))}
          </SegmentedControl>
        </>
      )}
    </div>
  );
}
